// utils/kql/kql.js
//
// KQL — the Keepr Query Language: parser, canonical serializer, AST helpers.
//
// DEPENDENCY-FREE AND PLATFORM-FREE ON PURPOSE. This file is vendored verbatim
// into keepr-web (`src/utils/kql/kql.js`) and kept in lock-step by the shared
// test-vector file next to it (`vectors.json`), which both repos' suites drive.
// Nothing here may require() anything — no Mongo, no Hapi, no Boom, no dayjs.
// The compiler half (compile.js) is where platform knowledge lives.
//
// Grammar (v1) — see docs/plans/query-language.md and docs/SCHEMA.md:
//
//   expr      := or
//   or        := and ('or' and)*
//   and       := unary ('and' unary)*
//   unary     := 'not' unary | '(' expr ')' | condition
//   condition := (path | facet) comparison
//   path      := segment ('.' segment)*        (segment: bare ident, "quoted",
//                                               or an element id #k7f3q2xa)
//   facet     := '@' (card-key | '"' card key '"')      (a root, never dotted)
//   comparison:= ('=' | '!=' | '>' | '>=' | '<' | '<=' | '~' | '!~') value
//              | ('in' | 'not in') '(' value (',' value)* ')'
//              | 'is' 'empty' | 'is' 'not' 'empty'
//              | 'is' value            (alias for '=')
//              | 'is' 'not' value      (alias for '!=')
//              | 'contains' value      (alias for '~')
//              | 'has' '(' expr ')'    (a repeating group's block — see
//                                       REPEATING GROUPS below)
//   value     := string | number | date | relative | bool | 'me' | ref
//   ref       := '{{' (element-name | element-id) '}}'
//                                             (ONE segment — no dot-walk)
//
// AST (the wire/storage shape):
//
//   { op: 'and' | 'or', args: [node, ...] }
//   { op: 'not', arg: node }
//   { op: 'cmp', path: ['resident', 'move-in-date'],
//     cmp: 'eq|ne|gt|gte|lt|lte|contains|ncontains|in|nin|empty|nempty|has',
//     value: { type: 'str|num|date|rel|bool|me|ref|list|block', v: ... },
//     facet: true,                      // optional; see below
//     forceElement: true }              // optional; see below
//
// A `ref` VALUE is the one right-hand side that is not a literal: `on-hand <=
// {{reorder-at}}` asks whether THIS RECORD's `on-hand` is at or below THIS
// RECORD's `reorder-at`. The grammar only records the name
// (`{ type: 'ref', v: 'reorder-at' }`) — or an element id's segment, `{{#k7f3q2xa}}`
// (`{ type: 'ref', v: '#k7f3q2xa' }`, see A REFERENCE BY ID below); resolving it against a card's elements,
// deciding whether the two types may be compared at all, and emitting the
// comparison are the compiler's (utils/kql/compile.js, compileValues.js).
//
// The spelling is deliberately the platform's existing merge-field syntax
// (utils/formula/formula.js `REF := '{{' name ('.' name)? '}}'`), so the
// platform gains no second vocabulary for the one concept. It costs the
// grammar nothing: `{` and `}` were "Unexpected character" errors before this
// and appear in no stored query. The braces self-delimit, so a name with
// spaces (`{{heart rate}}`) needs no second quoting rule — the single biggest
// practical win over a `$name` sigil, and the reason a bare word was never an
// option (every choice slug and card key is a bare word today, so
// `status = down` must not change meaning when somebody later adds an element
// called `down`; a query's meaning may never move with schema drift).
//
// ONE SEGMENT, and `{{a.b}}` is a parse error — this is where the spelling
// DIVERGES from formula.js, which allows one hop. A reference is evaluated
// against the record being tested, so there is no second document for
// `{{vehicle.weight}}` to read; dot-walk belongs on the LEFT, where each hop
// resolves through its own scoped query. The same refusal closes the
// automation-template footgun: a template (`{{item.qty}}`, `{{last.x}}`,
// `{{user.x}}`) is a VALUE and lives inside QUOTED text, where the parser
// never sees it — an unquoted one names an element that cannot exist, so the
// error points at the quoted form rather than guessing.
//
// WHERE G3 WOULD GO. Cross-collection references — "this vehicle's weight,
// where the vehicle lives in another collection" — are deferred, not refused
// on principle. When they land, they land as a SECOND SEGMENT on the LEFT
// (the path), never inside `{{...}}`: a ref stays one segment because it is
// evaluated against the record being tested, and that does not change when
// another collection becomes reachable. The left-hand path already resolves
// each hop through its own scoped query, which is the seam where a
// cross-collection hop's permission check belongs — a `{{...}}` has no such
// seam, and giving it one would mean a value that reads a document the
// caller may not be allowed to see. So: extend `path` in the parser and give
// compile.js a hop kind; leave this grammar's ref alone.
//
// `me` is a VALUE KEYWORD, like `today`: the signed-in caller. The grammar
// only records it (`{ type: 'me', v: 'me' }`); what it means is the
// compiler's — the caller's account id on a `user` element, the caller's
// linked item on a card-lookup element or a facet (utils/kql/compile.js).
// A value literally "me" must be quoted, exactly as `today` must.
//
// `forceElement` records that the first path segment was QUOTED and collides
// with a system field name (`card`, `set`, `tags`, `created`, `updated`, `id`, `text`) — the
// escape hatch for an element literally named `card`. The serializer re-emits
// the quotes so the meaning survives a round trip. Redundant quoting on a
// non-system name is dropped by the canonical form.
//
// `facet` records that the root was written `@<card-key>`: the condition is
// not about a FIELD at all but about a CARD — every item that points at it
// through any lookup, whatever those lookups are called. The key lives in
// `path[0]` (always exactly one segment) rather than inside the string, so no
// consumer can mistake a sigil for part of a name and build `elements.@people`
// out of it; the flag, not the spelling, is what tells a facet apart. A facet
// compares items, so only `= != in not in is empty is not empty` may follow
// one — the rest are parse errors. Compilation is the API's (utils/kql/
// compile.js); it is an ITEMS-only root, and the cards and collections
// subjects compile it to a no-match.
//
// DATE PARTS AND THE CREATOR (keepr-web docs/plans/analytics.md q20, PR AK).
// `created.weekday = mon`, `created.hour >= 9`, `created.month in (6, 7, 8)`,
// the same three on `updated`, and on a date or date-time element
// (`due.weekday`, `visit.hour`); `created.by = me` is who added the item. The
// grammar does not change for them — a part is a second path segment, which
// the parser has always read — and neither does the AST. What this file adds
// is the vocabulary every reader shares: which second segments are parts
// (DATE_PARTS, CREATOR_PART), what values a part reads (datePartNumber: a
// weekday by name, an hour 0–23, a month 1–12 or by name), its canonical
// spelling (datePartValue) and which values a comparison selects
// (datePartSet, Sunday first, as weeks start everywhere on the platform).
// What a two-segment path MEANS stays the compiler's: on `created`/`updated`
// always a part, on an element only when that element is a date or a
// date-time — on an item lookup the same segment is a dot-walk, as it always
// was — and the zone an instant is read in is the collection's.
//
// ELEMENT IDS (KPR-168; keepr-web docs/plans/element-ids.md §3b, ruled I3).
// `#k7f3q2xa` names ONE element by its immutable id — eight characters of
// `[a-z0-9]`, minted by the server and unique across the platform — whatever
// it is called now and whichever card first spells its name. It is one PATH
// SEGMENT, standing wherever an element name may: at the root
// (`#k7f3q2xa > 100`), after a hop (`#lookupid.#leafid00`), before a date
// part (`#k7f3q2xa.weekday`). In the AST it is the segment string itself,
// `'#k7f3q2xa'`: no element name can hold a `#` (queryContract's
// ELEMENT_NAME_RE), so a consumer that has not learned ids reads it as a name
// nothing defines — an unknown element, the closed answer — and never as
// another element. `#` meant nothing before this (an unexpected character),
// so no stored text changes meaning. Exactly eight lower-case letters or
// digits follow it, or the text is a parse error; a quoted `"#k7f3q2xa"` is
// TEXT: as a value it is a string, and as a field name it is refused (it
// would be an element NAME no element can have, and reading it as the id
// would make quoting mean nothing). What an id names — its definition, the
// cards that hold it, the name its values are stored under — is the
// compiler's (utils/kql/compile.js).
//
// A REFERENCE BY ID (element ids step 3, KPR-238; keepr-web docs/plans/
// element-ids-writers-and-closing.md ⚑ R1): `on-hand <= {{#k7f3q2xa}}` reads
// the element with that id on the same record. Inside the braces the same
// eight characters follow the `#`, or the text is a parse error at the `{{`;
// the AST holds the segment, `{ type: 'ref', v: '#k7f3q2xa' }`, so a reader
// that has not learned it reads a name nothing defines — the reference that
// cannot answer — never another element. `{` meant nothing inside a
// reference's name before this, so no stored text changes meaning; a
// reference by name reads exactly as before.
//
// REPEATING GROUPS (KPR-681, epic KPE-027 — ruled by the owner: a condition
// inside a scoped block must hold on ONE entry). A repeating group is an
// element whose value is a list of entries, each with its own columns.
//
//   ingredient has (name = butter and amount > 2)   one entry holds both
//   ingredient has (name = butter) and ingredient has (name = salt)
//                                                    two blocks: different
//                                                    entries may answer each
//   not ingredient has (name = nuts)                 no entry holds it
//   ingredient is empty / ingredient is not empty    no entries / some
//   ingredient.count >= 5                            how many entries
//
// THE BLOCK is a comparison whose value is a query: `{ op: 'cmp', path:
// ['ingredient'], cmp: 'has', value: { type: 'block', v: <node> } }`. A cmp,
// not an operator node of its own, so a reader that has not learned blocks
// still sees a condition on the GROUP — what it reads, what a rename of it
// moves, what a gate on it refuses — and never an empty node it can skip; a
// block's own conditions sit inside the value, where no reader looks for
// element names, so a COLUMN's name is never read as an element's. Inside the
// parentheses is ordinary KQL — `and`, `or`, `not`, parentheses, every
// operator and value — with these rules, each a parse error naming it:
//   - blocks do not nest (a group's entries hold no group: one level);
//   - each condition names ONE column, by name, "quoted name" or `#id`, or
//     one column's DATE PART (`given.weekday`, `given.hour`, `given.month` —
//     KPR-935; whether the column has one is the compiler's, as for an
//     element): no dot-walk or count inside a block yet;
//   - no facet, no unquoted system field (an entry has no card, tags or
//     dates of its own; a column literally called `card` is `"card"`), and
//     no `{{reference}}` (a block compares a column with a value);
//   - `has` follows ONE segment — a group of the item itself — never a
//     facet, a system field or a dot-walk; and a block holds a condition.
// `has` is a keyword only where an operator is read: as a value or an
// element name it is the text it always was, so no stored query changes
// meaning (`x has …` was a parse error before).
//
// THE COUNT is a second path segment, as a date part is: `ingredient.count`.
// What it means is the compiler's — the number of entries on a repeating
// group, a dot-walk into the record's `count` on an item lookup, as it always
// was — so the grammar does not change for it (isEntryCountPath reads it).
//
// THE CAP (MAX_CONDITIONS). A block counts as ONE condition for itself plus
// every condition inside it: `ingredient has (name = butter and amount > 2)`
// is three. The block is a clause of its own in what the query compiles to,
// and counting it keeps every spelling at least as dear as the flat one, so a
// block is never a way past the cap. The length cap is the text's, blocks
// included.
//
// The TEXT form is canonical for storage and URLs; the AST is an in-memory
// artifact. `parse(serialize(parse(x)))` is stable for every input.

'use strict';

// ---------------------------------------------------------------- constants

const MAX_QUERY_LENGTH = 2000;
const MAX_CONDITIONS = 50;
// Dot-walk hops PAST the root segment, so `a.b.c.d` (3 hops) is the deepest
// legal path. Mirrors MAX_RELATED_LOOKUP_HOPS in utils/itemRelUtils.js.
const MAX_PATH_HOPS = 3;
const MAX_PATH_SEGMENTS = MAX_PATH_HOPS + 1;

// The first path segment is one of these unless it was quoted. `set` is the
// element set an item's card carries (utils/kql/compile.js compileSet) — an
// element literally named `set` is reachable as `"set"`.
// `collection` (sub-collections): which collection of the scoping collection's
// family an item lives in — `collection = health` matches Health and anything
// beneath it, `collection = this` the scoping collection alone. An element
// named `collection` is reached quoted, like every other system name.
// `text` (KPR-739): the FREE-TEXT term — `text ~ "term"` searches every field
// of a record by one rule (keepr-api utils/freeText.js; the list's search
// box writes it). The cards, collections and packages subjects already read
// `text` as their own free text and ignore `forceElement`, so for them
// nothing changes; on items an element literally named `text` is `"text"`.
const SYSTEM_FIELDS = ['card', 'set', 'tags', 'notes', 'created', 'updated', 'id', 'collection', 'text'];
const SYSTEM_FIELD_SET = new Set(SYSTEM_FIELDS);

// A bare (unquoted) path segment. Element names may contain spaces
// (queryContract.ELEMENT_NAME_RE) — those must be quoted in KQL.
const IDENT_RE = /^[A-Za-z0-9][A-Za-z0-9_-]*$/;

// The bare form of a facet's card key, lexed straight after the `@`. Card keys
// are server-derived slugs, so this charset — the same one a bare path segment
// has — covers every key the platform writes; a legacy or hand-edited key with
// anything else in it is reachable as `@"some key"`.
const FACET_KEY_RE = /[A-Za-z0-9][A-Za-z0-9_-]*/y;

// What may follow a facet root. A facet compares ITEMS: a reference has no
// ordering and no substring, so `>`, `~` and their kin mean nothing here and
// are refused while the query is still text.
const FACET_CMPS = new Set(['eq', 'ne', 'in', 'nin', 'empty', 'nempty']);

const HEX_ID_RE = /^[0-9a-fA-F]{24}$/;

// An element id (see ELEMENT IDS in the header): the server's eight
// characters, and the `#` that spells one as a path segment.
const ELEMENT_ID_RE = /^[a-z0-9]{8}$/;
const ELEMENT_ID_SIGIL = '#';
const ELEMENT_ID_SEGMENT_RE = /^#[a-z0-9]{8}$/;
// What the lexer reads after a `#` before judging it: the run a bare word
// could hold, so `#k7f3q2xa1` and `#K7F3Q2XA` are refused whole rather than
// read as an id and a stray tail.
const ELEMENT_ID_RUN_RE = /[A-Za-z0-9_-]*/y;

// The inner name of a `{{...}}` reference. Restates utils/queryContract.js's
// ELEMENT_NAME_RE — the platform's element-name rule — because this file may
// not require() anything. It bans '{', '}' and '.' outright, which is what
// makes '}}' a safe terminator (no escape rule needed) and a dotted reference
// a parse error rather than a name nobody could have given an element.
const REF_NAME_RE = /^[A-Za-z0-9][A-Za-z0-9 _-]{0,99}$/;

// The automation template namespaces (utils/automations/template.js). A
// template is substituted structurally into QUOTED string values before a
// condition is compiled, so the parser never meets one; an UNQUOTED
// `{{item.qty}}` is therefore a reference to an element named "item.qty",
// which cannot exist. Named here so the dotted-reference error can say which
// spelling was probably meant, and so the automations layer can refuse a
// hand-built AST that smuggles one in as a ref node.
const TEMPLATE_NAMESPACES = ['item', 'last', 'user', 'me'];

// Relative dates. An offset is written either BARE (`-3d`, `+7d`, `-3mo`) or
// against the anchor it is measured from (`today-30d`, `now+2h`) — the two
// spellings are the SAME literal and both are canonical, so a stored query
// never changes text under the person who wrote it.
//
// An omitted anchor is `today`: a bare offset lands on UTC midnight, which is
// what makes `created = -3d` the calendar day it names rather than a 24-hour
// window ending at whatever o'clock the query ran. `now` is the instant, and
// the clock units `h` / `m` only mean something counted from a clock, so they
// are spelled `now±Nh` / `now±Nm` and nothing else — the rule
// utils/dateOptions.js already applies to a date element's own bounds.
const RELATIVE_RE = /^([-+])(\d+)(mo|[dwy])$/;
const RELATIVE_ANCHORED_RE = /^(today|now)(?:([-+])(\d+)(mo|[dwyhm]))?$/;
// A word that OPENS like one of the NEW forms must BE one: filing `+7h` or
// `now+2q` away as a plain string would bury a typo in a query that then
// quietly matches nothing. '+' is NEW to the lexer, so anything carrying one
// in these positions was a parse error before and can be claimed freely; '-'
// is NOT, and `now-open` / `today-only` are slugs somebody already stored (a
// choice value, a card key, a tag) and must keep parsing as the strings they
// have always been. So a MINUS anchor only opens the shape when a digit
// follows it — which still catches the typos that matter (`today-30`,
// `now-15x`). A bare `-3x` is left alone for the same reason.
const RELATIVE_SHAPE_RE = /^(?:\+|today\+|now\+|today-\d|now-\d)/;
const CLOCK_UNITS = new Set(['h', 'm']);

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?$/;

// The caller keyword. A bare `me` is the signed-in account (on a `user`
// element) or that account's linked item (on a lookup or a facet); the text
// "me" is `"me"`.
const ME = 'me';

// Words that can never be a BARE value — quoting is required to use them as
// text, otherwise `note = and` would silently swallow the conjunction.
const RESERVED_VALUE_WORDS = new Set(['and', 'or', 'not', 'is', 'in', 'contains', 'empty']);

const CMP_OPS = {
	'=': 'eq',
	'!=': 'ne',
	'>': 'gt',
	'>=': 'gte',
	'<': 'lt',
	'<=': 'lte',
	'~': 'contains',
	'!~': 'ncontains',
};

const CMP_TEXT = {
	eq: '=',
	ne: '!=',
	gt: '>',
	gte: '>=',
	lt: '<',
	lte: '<=',
	contains: '~',
	ncontains: '!~',
	in: 'in',
	nin: 'not in',
};

const VALUE_CMPS = new Set(['eq', 'ne', 'gt', 'gte', 'lt', 'lte', 'contains', 'ncontains']);
const LIST_CMPS = new Set(['in', 'nin']);
const NULLARY_CMPS = new Set(['empty', 'nempty']);

// ---- repeating groups (see REPEATING GROUPS in the header)

// The block's comparison and its value type. Kept out of CMP_TEXT and the
// sets above, which list what a FIELD may be compared with: a block is read
// by its own readers (isBlock, blockOf).
const BLOCK_CMP = 'has';
const BLOCK_VALUE = 'block';
// The second segment that counts a group's entries (`ingredient.count`).
const ENTRY_COUNT = 'count';

// Longest first so '>=' wins over '>'.
const SYMBOL_OPS = ['>=', '<=', '!=', '!~', '=', '>', '<', '~'];

// ---- date parts and the creator (see the header)

// The parts, as the second segment of a path. Lower case, as every system
// name is: `created.Weekday` is not one.
const DATE_PARTS = ['weekday', 'hour', 'month'];
// `created.by`: the account that added the item. Only on `created`.
const CREATOR_PART = 'by';
// The roots on which a part (and `by`) always means the system field.
const PART_ROOTS = ['created', 'updated'];
// A weekday is a NAME — never a number, because a number would have to pick
// between JavaScript's 0 = Sunday and ISO's 7 = Sunday. Its value is 0
// (Sunday) … 6 (Saturday), the platform's convention (a date element's
// `weekdays` option), and Sunday is first in an order, as a week is (q21).
const WEEKDAYS = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
const WEEKDAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const MONTHS = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec'];
const MONTH_NAMES = ['january', 'february', 'march', 'april', 'may', 'june', 'july', 'august', 'september', 'october', 'november', 'december'];
// Each part's values, smallest first.
const DATE_PART_RANGE = Object.freeze({ weekday: [0, 6], hour: [0, 23], month: [1, 12] });
const PART_DIGITS_RE = /^\d{1,2}$/;

// The grammar's number: digits, an optional decimal tail, no exponent.
const GRAMMAR_NUMBER_RE = /^-?\d+(?:\.\d+)?$/;
const EXPONENT_FORM_RE = /^(-?)(\d)(?:\.(\d+))?e([+-]\d+)$/;

// ------------------------------------------------------------------- errors

class KqlError extends Error {
	constructor(message, position) {
		super(message);
		this.name = 'KqlError';
		// Zero-based character offset into the query text. Handlers surface it
		// alongside the message in the 400 body.
		this.position = position || 0;
		this.isKqlError = true;
	}
}

// ---------------------------------------------------------- relative dates

/**
 * The pieces of a relative literal — `{ anchor: 'today'|'now', sign: 1|-1,
 * amount, unit }`, `unit` null for a bare anchor — or null when the text is
 * not one. The single place the grammar's date vocabulary is decided: the
 * lexer asks it what a word means, the serializer asks it whether a string
 * needs quotes, and the compilers ask it how wide an equality is.
 */
function relativeParts(text) {
	const v = String(text == null ? '' : text).toLowerCase();

	const bare = RELATIVE_RE.exec(v);
	if (bare) return { anchor: 'today', sign: bare[1] === '-' ? -1 : 1, amount: Number(bare[2]), unit: bare[3] };

	const anchored = RELATIVE_ANCHORED_RE.exec(v);
	if (!anchored) return null;
	const anchor = anchored[1];
	if (!anchored[2]) return { anchor, sign: 1, amount: 0, unit: null };
	const unit = anchored[4];
	// `today+30m` is a shape nothing can mean: a day has no clock to count from.
	if (CLOCK_UNITS.has(unit) && anchor !== 'now') return null;
	return { anchor, sign: anchored[2] === '-' ? -1 : 1, amount: Number(anchored[3]), unit };
}

/** True for every spelling the grammar reads as a relative date. */
function isRelativeLiteral(text) {
	return relativeParts(text) !== null;
}

/**
 * 'day' when a relative literal lands on UTC midnight (a bare offset or any
 * `today` anchor), 'instant' when it carries a clock (every `now` anchor),
 * null when the text is not relative. Equality against a 'day' covers the
 * whole calendar day; against an 'instant', the instant itself.
 */
function relativeGranularity(text) {
	const parts = relativeParts(text);
	if (!parts) return null;
	return parts.anchor === 'now' ? 'instant' : 'day';
}

/**
 * Resolves a relative date ('now', 'today', '-3d', '+7d', '-3mo',
 * 'today+7d', 'now-15m') to a Date. UTC throughout so the result does not
 * depend on server locale. Returns null for anything else.
 */
function resolveRelativeDate(value, now = new Date()) {
	const parts = relativeParts(value);
	if (!parts) return null;

	const out = parts.anchor === 'now'
		? new Date(now.getTime())
		: new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));

	const n = parts.sign * parts.amount;
	if (parts.unit === 'd') out.setUTCDate(out.getUTCDate() + n);
	else if (parts.unit === 'w') out.setUTCDate(out.getUTCDate() + n * 7);
	else if (parts.unit === 'mo') out.setUTCMonth(out.getUTCMonth() + n);
	else if (parts.unit === 'y') out.setUTCFullYear(out.getUTCFullYear() + n);
	else if (parts.unit === 'h') out.setUTCHours(out.getUTCHours() + n);
	else if (parts.unit === 'm') out.setUTCMinutes(out.getUTCMinutes() + n);
	return out;
}

// ------------------------------------------------------------------- lexer

const NUMBER_RE = /-?\d+(?:\.\d+)?/y;
// A word absorbs slugs, keywords, relative dates, ISO dates and datetimes.
// ':' is in the set so `2024-01-31T10:00:00` stays one token; '.' is NOT, so
// path dots always separate segments. '+' both OPENS a word (`+7d`) and sits
// inside one (`today+7d`), which is the only reason the character is legal at
// all — a '+' with no digit behind it is still an unexpected character.
const WORD_RE = /[-+]?[A-Za-z0-9][A-Za-z0-9_\-:+]*/y;
// A number token must not be the head of a longer word (`2024-01-31`, `-3mo`).
const WORD_TAIL_RE = /[A-Za-z0-9_\-:.]/;

/**
 * The body of a double-quoted run, starting at the opening quote. Shared by
 * the ordinary string token and the quoted form of a facet key (`@"some key"`)
 * so one escape rule — `\"` and `\\`, nothing else — covers both.
 */
function readQuoted(text, open) {
	let i = open + 1;
	let out = '';
	while (i < text.length) {
		const c = text[i];
		if (c === '\\') {
			const next = text[i + 1];
			if (next === '"' || next === '\\') { out += next; i += 2; continue; }
			throw new KqlError(
				`Invalid escape sequence \\${next === undefined ? '' : next} — only \\" and \\\\ are allowed.`, i);
		}
		if (c === '"') return { value: out, end: i + 1 };
		out += c;
		i++;
	}
	throw new KqlError('Unterminated quoted text — a closing " is missing.', open);
}

function tokenize(text) {
	const tokens = [];
	const n = text.length;
	let i = 0;

	while (i < n) {
		const ch = text[i];
		if (ch === ' ' || ch === '\t' || ch === '\n' || ch === '\r') { i++; continue; }

		const start = i;

		if (ch === '"') {
			const { value, end } = readQuoted(text, i);
			tokens.push({ type: 'str', value, raw: text.slice(start, end), start });
			i = end;
			continue;
		}

		// `@<card-key>` — a FACET root, the one place in the grammar a sigil
		// opens a path. The `@` decides what the token is, so a key that would
		// otherwise lex as a number or a keyword (`@2024`, `@not`) needs no
		// quotes; a key outside the bare charset takes the quoted form. A `@`
		// anywhere else is still an unexpected character and still points at
		// itself, so `note @ 2` reports the position it always did.
		if (ch === '@') {
			if (text[i + 1] === '"') {
				const { value, end } = readQuoted(text, i + 1);
				if (!value) throw new KqlError('A facet needs a card key — write @people, or @"some key".', start);
				tokens.push({ type: 'facet', value, raw: text.slice(start, end), start });
				i = end;
				continue;
			}
			FACET_KEY_RE.lastIndex = i + 1;
			const keyMatch = FACET_KEY_RE.exec(text);
			if (!keyMatch || keyMatch.index !== i + 1) {
				throw new KqlError(
					'A facet is "@" followed by a card key — write @people, or @"some key" for a key with other characters.',
					start);
			}
			i += 1 + keyMatch[0].length;
			tokens.push({ type: 'facet', value: keyMatch[0], raw: text.slice(start, i), start });
			continue;
		}

		// `{{element-name}}` — a REFERENCE to another element of the SAME
		// record (see the header block). Scanned with indexOf rather than a
		// regex so the work is linear even on unclosed input, and so the
		// error can point at the opening brace rather than at wherever a
		// backtracking match gave up.
		if (ch === '{') {
			if (text[i + 1] !== '{') {
				throw new KqlError('A reference is written {{element-name}} — a single "{" means nothing here.', start);
			}
			const close = text.indexOf('}}', i + 2);
			if (close === -1) {
				throw new KqlError('Unterminated reference — a closing "}}" is missing.', start);
			}
			const name = text.slice(i + 2, close).trim();
			if (!name) {
				throw new KqlError('A reference needs an element name — write {{reorder-at}}.', start);
			}
			if (name.includes('.')) {
				throw new KqlError(
					`A reference names ONE element of this same record, so ${JSON.stringify(name)} cannot be read — `
					+ 'dot-walk on the LEFT of the comparison instead. If you meant an automation template '
					+ `(${TEMPLATE_NAMESPACES.map(ns => `{{${ns}.…}}`).join(', ')}), quote it — "{{${name}}}" — `
					+ 'so it is a value rather than a reference.',
					start);
			}
			// `{{#k7f3q2xa}}` — the element by its ID (element ids step 3, ⚑ R1):
			// the same eight characters a path's `#` segment takes, judged whole.
			if (name[0] === ELEMENT_ID_SIGIL) {
				if (!ELEMENT_ID_SEGMENT_RE.test(name)) {
					throw new KqlError(
						`${JSON.stringify(name)} is not an element id — an element id is "#" and exactly eight `
						+ 'lower-case letters or digits, like {{#k7f3q2xa}}.',
						start);
				}
			}
			else if (!REF_NAME_RE.test(name)) {
				throw new KqlError(
					`${JSON.stringify(name)} is not an element name — letters, digits, spaces, "_" and "-", `
					+ 'starting with a letter or a digit.',
					start);
			}
			tokens.push({ type: 'ref', value: name, raw: text.slice(start, close + 2), start });
			i = close + 2;
			continue;
		}

		if (ch === '}') {
			throw new KqlError('Unexpected "}" — a reference is written {{element-name}}.', start);
		}

		// `#k7f3q2xa` — an ELEMENT ID (see the header). The whole run after
		// the `#` is judged, so a ninth character or a capital is refused
		// rather than split off.
		if (ch === ELEMENT_ID_SIGIL) {
			ELEMENT_ID_RUN_RE.lastIndex = i + 1;
			const run = ELEMENT_ID_RUN_RE.exec(text)[0];
			if (!ELEMENT_ID_RE.test(run)) {
				throw new KqlError(
					`${JSON.stringify(ELEMENT_ID_SIGIL + run)} is not an element id — an element id is "#" and exactly eight `
					+ 'lower-case letters or digits, like #k7f3q2xa.',
					start);
			}
			i += 1 + run.length;
			tokens.push({ type: 'elementId', value: ELEMENT_ID_SIGIL + run, raw: text.slice(start, i), start });
			continue;
		}

		if (ch === '(' || ch === ')' || ch === ',' || ch === '.') {
			tokens.push({ type: 'punct', value: ch, raw: ch, start });
			i++;
			continue;
		}

		NUMBER_RE.lastIndex = i;
		const numMatch = NUMBER_RE.exec(text);
		// An all-digit 24-character run is an ObjectId, not a number — ids are
		// first-class values here (`id = ...`, `card = ...`, card-lookups), and
		// a hex id that happens to contain no letters must not become 1.1e23.
		if (numMatch && numMatch.index === i && !HEX_ID_RE.test(numMatch[0])) {
			const after = text[i + numMatch[0].length];
			if (after === undefined || !WORD_TAIL_RE.test(after)) {
				const value = Number(numMatch[0]);
				// 309 digits and more read as Infinity, which no value equals and
				// the serializer cannot write back: refused here, as text.
				if (!Number.isFinite(value)) {
					throw new KqlError(`Number too large: ${numMatch[0].length} characters. Quote it to use it as text.`, start);
				}
				tokens.push({ type: 'num', value, raw: numMatch[0], start });
				i += numMatch[0].length;
				continue;
			}
		}

		WORD_RE.lastIndex = i;
		const wordMatch = WORD_RE.exec(text);
		if (wordMatch && wordMatch.index === i) {
			tokens.push({ type: 'word', value: wordMatch[0], raw: wordMatch[0], start });
			i += wordMatch[0].length;
			continue;
		}

		const symbol = SYMBOL_OPS.find(op => text.startsWith(op, i));
		if (symbol) {
			tokens.push({ type: 'op', value: symbol, raw: symbol, start });
			i += symbol.length;
			continue;
		}

		throw new KqlError(`Unexpected character ${JSON.stringify(ch)}.`, start);
	}

	return tokens;
}

function describe(token) {
	if (!token) return 'the end of the query';
	if (token.type === 'str') return `quoted text ${JSON.stringify(token.value)}`;
	if (token.type === 'ref') return `the reference ${JSON.stringify(token.raw)}`;
	return `${JSON.stringify(token.raw)}`;
}

// ------------------------------------------------------------------ parser

function peek(state, offset = 0) {
	return state.tokens[state.pos + offset];
}

function atWord(state, word) {
	const tok = peek(state);
	return !!tok && tok.type === 'word' && tok.value.toLowerCase() === word;
}

function atPunct(state, value) {
	const tok = peek(state);
	return !!tok && tok.type === 'punct' && tok.value === value;
}

function endPosition(state) {
	return state.text.length;
}

function expectPunct(state, value, what) {
	if (!atPunct(state, value)) {
		const tok = peek(state);
		throw new KqlError(`Expected ${JSON.stringify(value)}${what ? ' ' + what : ''} but found ${describe(tok)}.`,
			tok ? tok.start : endPosition(state));
	}
	state.pos++;
}

/** Splices same-op children so `(a and b) and c` canonicalizes to one node. */
function flatten(op, args) {
	const out = [];
	args.forEach(arg => {
		if (arg && arg.op === op) out.push(...arg.args);
		else out.push(arg);
	});
	return out;
}

function parseOr(state) {
	const args = [parseAnd(state)];
	while (atWord(state, 'or')) {
		state.pos++;
		args.push(parseAnd(state));
	}
	return args.length === 1 ? args[0] : { op: 'or', args: flatten('or', args) };
}

function parseAnd(state) {
	const args = [parseUnary(state)];
	while (atWord(state, 'and')) {
		state.pos++;
		args.push(parseUnary(state));
	}
	return args.length === 1 ? args[0] : { op: 'and', args: flatten('and', args) };
}

function parseUnary(state) {
	if (atWord(state, 'not')) {
		state.pos++;
		return { op: 'not', arg: parseUnary(state) };
	}
	return parsePrimary(state);
}

function parsePrimary(state) {
	const tok = peek(state);
	if (!tok) throw new KqlError('Unexpected end of query — a condition is missing.', endPosition(state));

	if (tok.type === 'punct' && tok.value === '(') {
		state.pos++;
		const inner = parseOr(state);
		expectPunct(state, ')', 'to close the group');
		return inner;
	}

	return parseCondition(state);
}

function parsePath(state) {
	const first = peek(state);
	const segments = [];
	let forceElement = false;

	// A facet is a WHOLE root: `@people` names a card, not a value to walk
	// into, so a dot after it is a parse error rather than a hop. (The lookups
	// it stands for are many and differently shaped; there is no one field for
	// `@people.city` to mean.)
	if (first && first.type === 'facet') {
		state.pos++;
		if (atPunct(state, '.')) {
			throw new KqlError(
				`A facet has no sub-fields: ${JSON.stringify(first.raw)} names a card, not a field to walk into.`,
				peek(state).start);
		}
		return { path: [first.value], forceElement: false, facet: true };
	}

	for (;;) {
		const tok = peek(state);
		if (!tok) throw new KqlError('Expected a field name.', endPosition(state));

		if (tok.type === 'str') {
			if (!tok.value) throw new KqlError('A field name cannot be empty.', tok.start);
			// Quoted, an id's spelling would be an element NAME — one no element
			// can have — and reading it as the id would make the quotes mean
			// nothing: refused, pointing at the spelling that names the element.
			if (ELEMENT_ID_SEGMENT_RE.test(tok.value)) {
				throw new KqlError(
					`${tok.raw} is quoted, so it is a name, and no element is called that — write ${tok.value} without quotes to name the element with that id.`,
					tok.start);
			}
			// A quoted FIRST segment always means an element, never a system
			// field. Only worth recording when the two actually collide.
			if (segments.length === 0 && SYSTEM_FIELD_SET.has(tok.value)) forceElement = true;
			segments.push(tok.value);
			state.pos++;
		}
		else if (tok.type === 'word' && IDENT_RE.test(tok.value)) {
			segments.push(tok.value);
			state.pos++;
		}
		// An element id is a segment wherever a name is one: the root, a hop's
		// leaf, the root of a date part (`#k7f3q2xa.weekday`).
		else if (tok.type === 'elementId') {
			segments.push(tok.value);
			state.pos++;
		}
		// A reference is a VALUE — the right-hand side reads this record's own
		// elements. On the left there is nothing for it to mean: the field
		// being compared is named plainly, quoted when it needs to be.
		else if (tok.type === 'ref') {
			throw new KqlError(
				`A reference belongs on the RIGHT of a comparison, not in a field position — `
				+ (ELEMENT_ID_SEGMENT_RE.test(tok.value)
					? `write ${tok.value} to name the field.`
					: `write ${JSON.stringify(tok.value)} (or "${tok.value}") to name the field.`),
				tok.start);
		}
		else {
			throw new KqlError(`Expected a field name but found ${describe(tok)}.`, tok.start);
		}

		if (atPunct(state, '.')) { state.pos++; continue; }
		break;
	}

	if (segments.length > MAX_PATH_SEGMENTS) {
		throw new KqlError(
			`Path is too deep: ${segments.length - 1} hops, maximum ${MAX_PATH_HOPS}.`,
			first ? first.start : 0);
	}

	return { path: segments, forceElement };
}

function wordValue(token) {
	const raw = token.value;
	const lower = raw.toLowerCase();

	if (RESERVED_VALUE_WORDS.has(lower)) {
		throw new KqlError(`${JSON.stringify(raw)} is a reserved word — quote it to use it as a value.`, token.start);
	}
	if (lower === 'true' || lower === 'false') return { type: 'bool', v: lower === 'true' };
	if (lower === ME) return { type: 'me', v: ME };
	if (isRelativeLiteral(lower)) return { type: 'rel', v: lower };
	if (RELATIVE_SHAPE_RE.test(lower)) {
		throw new KqlError(
			`${JSON.stringify(raw)} is not a relative date — write a signed offset (+7d, -30d, -3mo, -1y) ` +
			'or anchor it (today+7d, now-15m); h and m count only from now.', token.start);
	}
	if (DATE_RE.test(raw) || DATETIME_RE.test(raw)) return { type: 'date', v: raw };
	// Bare slug words are plain strings — lets choice values and card keys go
	// unquoted.
	return { type: 'str', v: raw };
}

function parseValue(state) {
	const tok = peek(state);
	if (!tok) throw new KqlError('Expected a value.', endPosition(state));
	if (tok.type === 'str') { state.pos++; return { type: 'str', v: tok.value }; }
	if (tok.type === 'num') { state.pos++; return { type: 'num', v: tok.value }; }
	// The reference carries the NAME only — or the element id's segment,
	// `#k7f3q2xa`, as a path does — never `elements.`: the same reason a facet
	// carries the bare card key, no consumer can splice a sigil into a Mongo
	// field path out of it.
	if (tok.type === 'ref') {
		// Inside a block a condition compares one entry's column with a value;
		// a reference to another element (of the entry? of the item?) has no
		// one reading there yet.
		if (state.block) {
			throw new KqlError(
				`A reference cannot stand inside has (…): ${tok.raw} would compare one entry's column with another element — compare the column with a value.`,
				tok.start);
		}
		state.pos++;
		return { type: 'ref', v: tok.value };
	}
	if (tok.type === 'word') { state.pos++; return wordValue(tok); }
	// An element id names a FIELD; as a value it means nothing. The text of
	// one is the quoted string.
	if (tok.type === 'elementId') {
		throw new KqlError(
			`${tok.raw} names an element, so it belongs on the left of a comparison — quote it ("${tok.value}") to compare with the text.`,
			tok.start);
	}
	throw new KqlError(`Expected a value but found ${describe(tok)}.`, tok.start);
}

function parseList(state) {
	const open = peek(state);
	expectPunct(state, '(', 'to open the value list');
	const values = [];
	if (atPunct(state, ')')) {
		throw new KqlError('A value list must contain at least one value.', open ? open.start : endPosition(state));
	}
	for (;;) {
		values.push(parseValue(state));
		if (atPunct(state, ',')) { state.pos++; continue; }
		break;
	}
	expectPunct(state, ')', 'to close the value list');
	return { type: 'list', v: values };
}

function parseComparison(state) {
	const tok = peek(state);
	if (!tok) throw new KqlError('Expected an operator.', endPosition(state));

	if (tok.type === 'op') {
		state.pos++;
		return { cmp: CMP_OPS[tok.value], value: parseValue(state) };
	}

	if (tok.type === 'word') {
		const word = tok.value.toLowerCase();
		if (word === 'is') {
			state.pos++;
			if (atWord(state, 'not')) {
				state.pos++;
				if (atWord(state, 'empty')) { state.pos++; return { cmp: 'nempty' }; }
				return { cmp: 'ne', value: parseValue(state) };
			}
			if (atWord(state, 'empty')) { state.pos++; return { cmp: 'empty' }; }
			return { cmp: 'eq', value: parseValue(state) };
		}
		if (word === 'contains') {
			state.pos++;
			return { cmp: 'contains', value: parseValue(state) };
		}
		if (word === 'in') {
			state.pos++;
			return { cmp: 'in', value: parseList(state) };
		}
		if (word === 'not') {
			state.pos++;
			if (!atWord(state, 'in')) {
				const after = peek(state);
				throw new KqlError(`Expected "in" after "not" but found ${describe(after)}.`,
					after ? after.start : endPosition(state));
			}
			state.pos++;
			return { cmp: 'nin', value: parseList(state) };
		}
	}

	throw new KqlError(`Expected an operator but found ${describe(tok)}.`, tok.start);
}

/**
 * One more condition against the cap. A block counts itself and each
 * condition inside it (THE CAP in the header), so once a query holds one the
 * refusal says so.
 */
function countCondition(state, first) {
	state.conditions++;
	if (state.conditions > MAX_CONDITIONS) {
		throw new KqlError(
			`Query has too many conditions: maximum ${MAX_CONDITIONS}`
			+ (state.blocks ? ' — a has (…) block counts as one, and so does each condition inside it.' : '.'),
			first ? first.start : 0);
	}
}

/**
 * `<group> has ( expr )` — a repeating group's block (REPEATING GROUPS in the
 * header), read once the path before `has` is. Every refusal is a sentence
 * naming the rule it keeps.
 */
function parseBlock(state, first, { path, forceElement, facet }) {
	const hasToken = peek(state);
	if (state.block) {
		throw new KqlError(
			'has (…) blocks do not nest: an entry of a repeating group holds no group of its own (one level).',
			hasToken.start);
	}
	if (facet) {
		throw new KqlError(
			`${JSON.stringify(first.raw)} names a card, and has (…) follows the name of a repeating group — write ingredient has (name = butter).`,
			hasToken.start);
	}
	if (path.length !== 1) {
		throw new KqlError(
			`has (…) follows a repeating group of this item, named by itself — ${JSON.stringify(path.join('.'))} walks into another record or part.`,
			hasToken.start);
	}
	if (!forceElement && SYSTEM_FIELD_SET.has(path[0])) {
		throw new KqlError(
			`"${path[0]}" is a system field, and has (…) follows the name of a repeating group — quote it ("${path[0]}") for a group called that.`,
			first.start);
	}
	state.pos++;
	// The block counts itself before what it holds.
	state.blocks++;
	countCondition(state, first);
	const open = peek(state);
	expectPunct(state, '(', 'to open the has (…) block');
	if (atPunct(state, ')')) {
		throw new KqlError('A has (…) block needs a condition — write ingredient has (name = butter).', open.start);
	}
	state.block = hasToken;
	const inner = parseOr(state);
	state.block = null;
	expectPunct(state, ')', 'to close the has (…) block');
	const node = { op: 'cmp', path, cmp: BLOCK_CMP, value: { type: BLOCK_VALUE, v: inner } };
	if (forceElement) node.forceElement = true;
	return node;
}

/**
 * A column's DATE PART inside a block (KPR-935): `given.weekday`, exactly two
 * segments, the second one of DATE_PARTS — as a part is spelled at the top
 * level. Whether the column CAN have one (a date or a date-time) is the
 * compiler's, as it is for an element.
 */
function isColumnPartPath(path) {
	return Array.isArray(path) && path.length === 2 && DATE_PARTS.indexOf(path[1]) !== -1;
}

/**
 * Inside a block every condition reads ONE column of one entry, or one
 * column's date part (KPR-935): no facet, no dot-walk (nor a count), no
 * unquoted system field. Refused where it is written, each with the sentence
 * that names the rule.
 */
function assertColumnCondition(state, first, { path, forceElement, facet }) {
	if (facet) {
		throw new KqlError(
			`Inside has (…) every condition reads a column of one entry, and ${JSON.stringify(first.raw)} names a card.`,
			first.start);
	}
	if (path.length !== 1 && !isColumnPartPath(path)) {
		throw new KqlError(
			`Inside has (…) name one column, or a date part of one (given.weekday) — ${JSON.stringify(path.join('.'))} walks into another record, which a block does not do yet.`,
			first.start);
	}
	if (!forceElement && SYSTEM_FIELD_SET.has(path[0])) {
		// `text` is the search term (KPR-739), which reads the record — never
		// one entry — so its sentence says that rather than "no text".
		const why = path[0] === 'text'
			? 'and a search (text ~ …) reads the whole record, never one entry'
			: `and an entry has no ${path[0]} of its own`;
		throw new KqlError(
			`"${path[0]}" is not a column: inside has (…) every condition reads a column of the entry, ${why}. Quote it ("${path[0]}") to name a column called that.`,
			first.start);
	}
}

function parseCondition(state) {
	const first = peek(state);
	const { path, forceElement, facet } = parsePath(state);
	if (atWord(state, BLOCK_CMP)) return parseBlock(state, first, { path, forceElement, facet });
	if (state.block) assertColumnCondition(state, first, { path, forceElement, facet });
	const opToken = peek(state);
	const { cmp, value } = parseComparison(state);

	// A facet compares ITEMS — an id, a title, a list of either, or nothing at
	// all. Ordering and substrings belong to values, and a reference is not
	// one, so the other operators are refused here rather than compiled into a
	// filter that could only ever match nothing.
	if (facet && !FACET_CMPS.has(cmp)) {
		throw new KqlError(
			`${JSON.stringify(opToken ? opToken.raw : '')} cannot compare a facet — a facet compares items, not values. `
			+ 'Use =, !=, in (...), not in (...), is empty or is not empty.',
			opToken ? opToken.start : endPosition(state));
	}

	countCondition(state, first);

	const node = { op: 'cmp', path, cmp };
	if (value !== undefined) node.value = value;
	if (facet) node.facet = true;
	if (forceElement) node.forceElement = true;
	return node;
}

/**
 * parse(text) -> ast. Throws a KqlError carrying `position` (character offset)
 * and a human-readable `message`; callers turn that into a 400.
 */
function parse(text) {
	if (typeof text !== 'string') throw new KqlError('A query must be text.', 0);
	if (text.length > MAX_QUERY_LENGTH) {
		throw new KqlError(`Query is too long: ${text.length} characters, maximum ${MAX_QUERY_LENGTH}.`, MAX_QUERY_LENGTH);
	}

	const tokens = tokenize(text);
	if (!tokens.length) throw new KqlError('Query is empty.', 0);

	// `block`: the `has` token of the block being read (null outside one);
	// `blocks`: how many the query has opened so far (the cap's words).
	const state = { tokens, pos: 0, text, conditions: 0, block: null, blocks: 0 };
	const node = parseOr(state);

	if (state.pos < tokens.length) {
		const tok = tokens[state.pos];
		throw new KqlError(`Unexpected ${describe(tok)}.`, tok.start);
	}

	return node;
}

// -------------------------------------------------------------- serializer

const PREC = { or: 1, and: 2, not: 3, cmp: 4 };

function quote(text) {
	return '"' + String(text).replace(/\\/g, '\\\\').replace(/"/g, '\\"') + '"';
}

/**
 * True when a path segment survives a round trip unquoted. Matching IDENT_RE is
 * necessary but NOT sufficient — two identifier-shaped names lex back as
 * something else entirely:
 *   - an all-digit run becomes a NUMBER token (`2024 = x` is not a condition),
 *     unless it is exactly 24 characters, which the lexer reads as an ObjectId;
 *   - `not` is taken by the unary parser before a path is ever read.
 * Both are legal element names (queryContract.ELEMENT_NAME_RE), so both must be
 * quoted on the way out.
 */
function isBareSegment(segment) {
	if (!IDENT_RE.test(segment)) return false;
	if (/^\d+$/.test(segment) && !HEX_ID_RE.test(segment)) return false;
	if (segment.toLowerCase() === 'not') return false;
	return true;
}

function serializeSegment(segment, isFirst, forceElement) {
	// An element id is written as it is, never quoted: quoted, it would be a
	// name (and the parser refuses that spelling). It never collides with a
	// system field, so `forceElement` has nothing to say about it.
	if (isElementIdSegment(segment)) return segment;
	if (isFirst && forceElement) return quote(segment);
	return isBareSegment(segment) ? segment : quote(segment);
}

/**
 * A facet root. Only the key follows the `@`, and the `@` has already told the
 * lexer what it is reading, so the only keys that need quoting are the ones
 * outside the bare charset — `@not` and `@2024` are safe as they stand, unlike
 * the same spellings as path segments. Redundant quotes are dropped, the way
 * they are everywhere else in the canonical form.
 */
function serializeFacet(key) {
	return '@' + (IDENT_RE.test(key) ? key : quote(key));
}

/**
 * A finite number in the grammar's own digits (TODO § 0zc). The lexer's
 * number is `-?\d+(\.\d+)?` and has no exponent, but `String(x)` writes one
 * below 1e-6 and from 1e21 up: `1.5e-7` lexed back as `1` and an unexpected
 * `.`, so a stored query's canonical text was text its own parser refused.
 * The exponent form is written out positionally instead — the same decimal,
 * so the parse reads back exactly the same double — and twenty-four digits
 * alone, which the lexer reads as an object id, keep a `.0` tail so they stay
 * a number. NaN and the infinities have no spelling: a malformed AST.
 * (Proven over 20,000 random doubles, the subnormals and the largest double
 * by charts A2a, whose doors carried it first.)
 */
function numberText(v) {
	const x = typeof v === 'number' ? v : Number(v);
	if (!Number.isFinite(x)) throw new KqlError(`Malformed AST: ${JSON.stringify(v)} is not a number the grammar can write.`, 0);
	const text = String(x);
	if (GRAMMAR_NUMBER_RE.test(text)) return text;
	const m = EXPONENT_FORM_RE.exec(text);
	if (!m) throw new KqlError(`Malformed AST: ${JSON.stringify(v)} is not a number the grammar can write.`, 0);
	const sign = m[1];
	const digits = m[2] + (m[3] || '');
	const point = 1 + Number(m[4]);
	let out;
	if (point <= 0) out = `0.${'0'.repeat(-point)}${digits}`;
	else if (point >= digits.length) out = digits + '0'.repeat(point - digits.length);
	else out = `${digits.slice(0, point)}.${digits.slice(point)}`;
	if (HEX_ID_RE.test(out)) out += '.0';
	return sign + out;
}

// ---- date parts and the creator: the readers

/**
 * The date part a condition names — `'weekday'`, `'hour'` or `'month'` — when
 * its path is a root and one of DATE_PARTS, else null. Whether the root CAN
 * have parts (`created`, `updated`, a date or date-time element) is the
 * compiler's to decide; on an item lookup the same path is a dot-walk.
 */
function datePartOf(node) {
	if (!node || node.op !== 'cmp' || node.facet || !Array.isArray(node.path) || node.path.length !== 2) return null;
	return DATE_PARTS.indexOf(node.path[1]) !== -1 ? node.path[1] : null;
}

/** True for `created.by` — the creator — and not for a quoted `"created".by`. */
function isCreatorPath(node) {
	return !!node && node.op === 'cmp' && !node.facet && !node.forceElement && Array.isArray(node.path)
		&& node.path.length === 2 && node.path[0] === 'created' && node.path[1] === CREATOR_PART;
}

/** True when a part on this condition always means the system field (`created.hour`, `updated.month`). */
function isSystemDatePart(node) {
	return datePartOf(node) !== null && !node.forceElement && PART_ROOTS.indexOf(node.path[0]) !== -1;
}

/**
 * The value a date-part literal names, as the part's integer — a weekday 0
 * (Sunday) … 6, an hour 0 … 23, a month 1 … 12 — or null when the literal
 * names none (an unknown word, a number out of range, a weekday written as a
 * number, a date, `me`). Case does not matter: `Mon`, `monday`, `MAR`.
 */
function datePartNumber(part, value) {
	if (!value || typeof value !== 'object' || !Object.prototype.hasOwnProperty.call(DATE_PART_RANGE, part)) return null;
	const [lo, hi] = DATE_PART_RANGE[part];
	const inRange = n => (Number.isInteger(n) && n >= lo && n <= hi ? n : null);
	if (value.type === 'num') return part === 'weekday' ? null : inRange(value.v);
	if (value.type !== 'str') return null;
	const text = String(value.v).trim().toLowerCase();
	if (part === 'weekday') {
		const at = WEEKDAYS.indexOf(text) !== -1 ? WEEKDAYS.indexOf(text) : WEEKDAY_NAMES.indexOf(text);
		return at === -1 ? null : at;
	}
	if (PART_DIGITS_RE.test(text)) return inRange(Number(text));
	if (part === 'month') {
		const at = MONTHS.indexOf(text) !== -1 ? MONTHS.indexOf(text) : MONTH_NAMES.indexOf(text);
		return at === -1 ? null : at + 1;
	}
	return null;
}

/** The canonical literal for a part's value: a weekday's short name (`mon`), an hour's or a month's number. Null out of range. */
function datePartValue(part, n) {
	if (!Object.prototype.hasOwnProperty.call(DATE_PART_RANGE, part)) return null;
	const [lo, hi] = DATE_PART_RANGE[part];
	if (!Number.isInteger(n) || n < lo || n > hi) return null;
	return part === 'weekday' ? { type: 'str', v: WEEKDAYS[n] } : { type: 'num', v: n };
}

/**
 * A range bound on a part, as a number in the part's own scale: a weekday by
 * name only (0 = Sunday), an hour or a month by any finite number (`< 24`,
 * `> -1`, `<= 9.5`), a month also by name. Null when the bound reads as none.
 */
function datePartBound(part, value) {
	if (part === 'weekday') return datePartNumber(part, value);
	if (!value || typeof value !== 'object') return null;
	if (value.type === 'num') return Number.isFinite(value.v) ? value.v : null;
	if (value.type !== 'str') return null;
	const text = String(value.v).trim();
	if (/^-?\d+(?:\.\d+)?$/.test(text)) return Number(text);
	return part === 'month' ? datePartNumber(part, value) : null;
}

/**
 * The part values a POSITIVE comparison selects, ascending: `eq` and `in`
 * the members the part reads — each member read on its own, so a member it
 * cannot read (`3` as a weekday, `25` as an hour) selects nothing and the
 * others still count; a range every value on its side in the part's order
 * (Sunday first, hour 0 first, January first), the bound compared where it
 * lies, inside the part's values or past them (`created.hour < 24` is every
 * hour, `> 23` none); `empty` none and `nempty` all. Null when the
 * comparison has no reading for a part — `~`, a reference, a range bound
 * that reads as none — or for a negated operator, which the caller asks as
 * the `not` of its positive half.
 */
function datePartSet(part, cmp, value) {
	if (!Object.prototype.hasOwnProperty.call(DATE_PART_RANGE, part)) return null;
	const [lo, hi] = DATE_PART_RANGE[part];
	const all = [];
	for (let n = lo; n <= hi; n++) all.push(n);
	if (cmp === 'empty') return [];
	if (cmp === 'nempty') return all;
	if (cmp === 'eq' || cmp === 'in') {
		const members = value && value.type === 'list' ? value.v : [value];
		const out = [];
		for (const member of members || []) {
			const n = datePartNumber(part, member);
			if (n !== null && out.indexOf(n) === -1) out.push(n);
		}
		return out.sort((a, b) => a - b);
	}
	const bound = datePartBound(part, value);
	if (bound === null) return null;
	switch (cmp) {
		case 'gt': return all.filter(n => n > bound);
		case 'gte': return all.filter(n => n >= bound);
		case 'lt': return all.filter(n => n < bound);
		case 'lte': return all.filter(n => n <= bound);
		default: return null;
	}
}

/** True when a string value survives a round trip unquoted. */
function isBareString(value) {
	if (!IDENT_RE.test(value)) return false;
	const lower = value.toLowerCase();
	if (RESERVED_VALUE_WORDS.has(lower)) return false;
	if (lower === 'true' || lower === 'false') return false;
	if (lower === ME) return false;
	if (isRelativeLiteral(lower)) return false;
	if (DATE_RE.test(value) || DATETIME_RE.test(value)) return false;
	// An all-digit run would lex back as a number — unless it is exactly 24
	// characters, which the lexer reads as an ObjectId string.
	if (/^\d+$/.test(value) && !HEX_ID_RE.test(value)) return false;
	return true;
}

function serializeValue(value) {
	if (!value || typeof value !== 'object') throw new KqlError('Malformed AST: missing value.', 0);
	switch (value.type) {
		case 'str':
			return isBareString(value.v) ? value.v : quote(value.v);
		case 'num':
			return numberText(value.v);
		case 'bool':
			return value.v ? 'true' : 'false';
		case 'me':
			return ME;
		// `}}` self-terminates and REF_NAME_RE bans both braces, so the name
		// never needs quoting however it is spelled — `{{heart rate}}` round
		// trips as it stands. Inner whitespace was trimmed at lex time, so
		// `{{ qty }}` canonicalizes to `{{qty}}`. A HAND-BUILT node is checked
		// here rather than trusted: a name outside the element charset is the
		// one thing that could emit text this parser reads back as something
		// else, and round-trip stability is the file's standing promise. An
		// id (`{{#k7f3q2xa}}`) is written as it is, as a path's `#` segment is.
		case 'ref':
			if (!REF_NAME_RE.test(String(value.v)) && !ELEMENT_ID_SEGMENT_RE.test(String(value.v))) {
				throw new KqlError(`Malformed AST: ${JSON.stringify(value.v)} is not an element name or id.`, 0);
			}
			return '{{' + value.v + '}}';
		case 'rel':
			return String(value.v).toLowerCase();
		case 'date':
			return String(value.v);
		case 'list':
			return '(' + value.v.map(serializeValue).join(', ') + ')';
		default:
			throw new KqlError(`Malformed AST: unknown value type ${JSON.stringify(value.type)}.`, 0);
	}
}

function serializeCmp(node) {
	if (!Array.isArray(node.path) || !node.path.length) {
		throw new KqlError('Malformed AST: a condition needs a path.', 0);
	}
	if (node.facet && node.path.length !== 1) {
		throw new KqlError('Malformed AST: a facet is one card key and nothing else.', 0);
	}
	const path = node.facet
		? serializeFacet(node.path[0])
		: node.path
			.map((segment, i) => serializeSegment(segment, i === 0, node.forceElement))
			.join('.');

	if (node.cmp === 'empty') return `${path} is empty`;
	if (node.cmp === 'nempty') return `${path} is not empty`;
	if (node.cmp === BLOCK_CMP) return `${path} has (${serializeBlock(node)})`;

	const op = CMP_TEXT[node.cmp];
	if (!op) throw new KqlError(`Malformed AST: unknown comparison ${JSON.stringify(node.cmp)}.`, 0);

	if (LIST_CMPS.has(node.cmp)) {
		if (!node.value || node.value.type !== 'list') {
			throw new KqlError(`Malformed AST: ${node.cmp} needs a list value.`, 0);
		}
		return `${path} ${op} ${serializeValue(node.value)}`;
	}

	return `${path} ${op} ${serializeValue(node.value)}`;
}

/**
 * The inside of a `has (…)` block. A HAND-BUILT block is checked against the
 * rules the parser keeps (REPEATING GROUPS in the header) rather than
 * trusted: text that broke one would be text this parser refuses, and
 * round-trip stability is the file's standing promise.
 */
function serializeBlock(node) {
	if (node.facet || node.path.length !== 1) throw new KqlError('Malformed AST: has (…) follows one group name.', 0);
	// A group spelled like a system field is written quoted, which only
	// `forceElement` asks for; unquoted it would read back as the field.
	if (!node.forceElement && SYSTEM_FIELD_SET.has(node.path[0])) throw new KqlError('Malformed AST: a group named like a system field needs forceElement.', 0);
	const value = node.value;
	if (!value || value.type !== BLOCK_VALUE || !value.v || typeof value.v !== 'object') {
		throw new KqlError('Malformed AST: has needs a block value.', 0);
	}
	assertBlockInner(value.v);
	return serializeNode(value.v, 0);
}

function valueHasRef(value) {
	if (!value || typeof value !== 'object') return false;
	if (value.type === 'ref') return true;
	return value.type === 'list' && (value.v || []).some(valueHasRef);
}

function assertBlockInner(node) {
	if (!node || typeof node !== 'object') throw new KqlError('Malformed AST: not a node.', 0);
	if (node.op === 'and' || node.op === 'or') { (node.args || []).forEach(assertBlockInner); return; }
	if (node.op === 'not') { assertBlockInner(node.arg); return; }
	if (node.op !== 'cmp') return; // serializeNode refuses it
	if (node.cmp === BLOCK_CMP) throw new KqlError('Malformed AST: has (…) blocks do not nest.', 0);
	if (node.facet) throw new KqlError('Malformed AST: a facet cannot stand inside a block.', 0);
	if (!Array.isArray(node.path) || (node.path.length !== 1 && !isColumnPartPath(node.path))) throw new KqlError('Malformed AST: a condition inside a block names one column, or a date part of one.', 0);
	if (!node.forceElement && SYSTEM_FIELD_SET.has(node.path[0])) throw new KqlError('Malformed AST: a system field cannot stand inside a block.', 0);
	if (valueHasRef(node.value)) throw new KqlError('Malformed AST: a reference cannot stand inside a block.', 0);
}

function serializeNode(node, parentPrec) {
	if (!node || typeof node !== 'object') throw new KqlError('Malformed AST: not a node.', 0);

	if (node.op === 'and' || node.op === 'or') {
		if (!Array.isArray(node.args) || !node.args.length) {
			throw new KqlError(`Malformed AST: ${node.op} needs args.`, 0);
		}
		if (node.args.length === 1) return serializeNode(node.args[0], parentPrec);
		const prec = PREC[node.op];
		const text = node.args.map(arg => serializeNode(arg, prec)).join(node.op === 'or' ? ' or ' : ' and ');
		return prec < parentPrec ? `(${text})` : text;
	}

	if (node.op === 'not') {
		return 'not ' + serializeNode(node.arg, PREC.not);
	}

	if (node.op === 'cmp') return serializeCmp(node);

	throw new KqlError(`Malformed AST: unknown op ${JSON.stringify(node.op)}.`, 0);
}

/** serialize(ast) -> canonical text. Round-trip stable with parse(). */
function serialize(ast) {
	return serializeNode(ast, 0);
}

// ------------------------------------------------------------- ast helpers

function isHexId(value) {
	return typeof value === 'string' && HEX_ID_RE.test(value);
}

function isSystemField(node) {
	return !node.facet && !node.forceElement && node.path.length === 1 && SYSTEM_FIELD_SET.has(node.path[0]);
}

// ---- element ids (see ELEMENT IDS in the header)

/** True for a path segment that names an element by id (`'#k7f3q2xa'`). */
function isElementIdSegment(segment) {
	return typeof segment === 'string' && ELEMENT_ID_SEGMENT_RE.test(segment);
}

/** The id a path segment names (`'#k7f3q2xa'` → `'k7f3q2xa'`), or null when it is a name. */
function elementIdOf(segment) {
	return isElementIdSegment(segment) ? segment.slice(ELEMENT_ID_SIGIL.length) : null;
}

/**
 * The path segment for an element id (`'k7f3q2xa'` → `'#k7f3q2xa'`). An id
 * of any other shape is a programming error, refused rather than written as
 * text the parser would read back as something else.
 */
function elementIdSegment(id) {
	const text = String(id == null ? '' : id);
	if (!ELEMENT_ID_RE.test(text)) throw new KqlError(`Malformed AST: ${JSON.stringify(text)} is not an element id.`, 0);
	return ELEMENT_ID_SIGIL + text;
}

/**
 * Every element id a query names — in a path, or in a `{{#k7f3q2xa}}`
 * reference (element ids step 3, ⚑ R1) — deduplicated, in first-seen order
 * (without the `#`), a comparison's path before its references. The reader
 * the web's labels and the server's checks share: a facet names a card, and
 * a literal value is never an id.
 */
function elementIdsOf(node) {
	const ids = [];
	const add = segment => {
		const id = elementIdOf(segment);
		if (id !== null && !ids.includes(id)) ids.push(id);
	};
	const addValue = value => {
		if (!value || typeof value !== 'object') return;
		if (value.type === 'ref') add(value.v);
		else if (value.type === 'list') (value.v || []).forEach(addValue);
	};
	const walk = n => {
		if (!n || typeof n !== 'object') return;
		if (n.op === 'not') walk(n.arg);
		else if (n.op === 'and' || n.op === 'or') (n.args || []).forEach(walk);
		else if (n.op === 'cmp') {
			if (!n.facet && Array.isArray(n.path)) n.path.forEach(add);
			addValue(n.value);
		}
	};
	walk(node);
	return ids;
}

/** Root segment resolution: system field name, or null when it's an element. */
function systemFieldOf(node) {
	// A facet root is neither a system field nor an element name — `@card`
	// asks about the card `card`, not about the system field.
	if (node.facet || node.forceElement) return null;
	return SYSTEM_FIELD_SET.has(node.path[0]) ? node.path[0] : null;
}

/**
 * The card key a facet condition names, or null when the node is not a facet.
 * The one reader every consumer should use: the flag decides, never the
 * spelling of `path[0]`.
 */
function facetKeyOf(node) {
	if (!node || !node.facet || !Array.isArray(node.path) || node.path.length !== 1) return null;
	return node.path[0];
}

/** True when any value in the AST is the `me` keyword (a query that needs a caller). */
function usesMe(node) {
	if (!node || typeof node !== 'object') return false;
	if (node.op === 'not') return usesMe(node.arg);
	if (node.op === 'and' || node.op === 'or') return (node.args || []).some(usesMe);
	if (node.op === 'cmp') return valueUsesMe(node.value);
	return false;
}

function valueUsesMe(value) {
	if (!value || typeof value !== 'object') return false;
	if (value.type === 'me') return true;
	if (value.type === 'list') return (value.v || []).some(valueUsesMe);
	// A block's conditions ask about the caller too (`assignee = me` on a
	// user column).
	if (value.type === BLOCK_VALUE) return usesMe(value.v);
	return false;
}

// ---- repeating groups: the readers (see REPEATING GROUPS in the header)

/** True for a `<group> has (…)` condition. */
function isBlock(node) {
	return !!node && node.op === 'cmp' && node.cmp === BLOCK_CMP && !!node.value && node.value.type === BLOCK_VALUE;
}

/** The query inside a `has (…)` block — conditions on the group's COLUMNS — or null for any other node. */
function blockOf(node) {
	return isBlock(node) && node.value.v && typeof node.value.v === 'object' ? node.value.v : null;
}

/**
 * The COLUMN a condition inside a block reads, as written — a name or an
 * `#id` — for the bare column (`name = butter`) and for its date part
 * (`given.weekday = mon`, KPR-935) alike; null for anything else (a facet, a
 * longer path, a block). The one reader every walker of a block's columns
 * asks, so a part's column is read wherever the column is.
 */
function blockColumnOf(node) {
	if (!node || node.op !== 'cmp' || node.facet || !Array.isArray(node.path) || isBlock(node)) return null;
	if (node.path.length === 1 || isColumnPartPath(node.path)) return node.path[0];
	return null;
}

/**
 * True for `<root>.count`: the number of entries when the root is a repeating
 * group. The grammar cannot know that — on an item lookup the same path is a
 * dot-walk into the record's `count`, as it always was — so, like a date
 * part, what it means is the compiler's. Never a system field's (`created.count`).
 */
function isEntryCountPath(node) {
	return !!node && node.op === 'cmp' && !node.facet && Array.isArray(node.path) && node.path.length === 2
		&& node.path[1] === ENTRY_COUNT && systemFieldOf(node) === null;
}

/**
 * True when any value in the AST is a `{{...}}` reference — a query that
 * compares two elements of the same record, and so cannot be answered by an
 * index (utils/kql/compile.js emits `$expr` for it).
 */
function usesRef(node) {
	return refNamesOf(node).length > 0;
}

/**
 * Every element referenced with `{{...}}` anywhere in the AST, as written — a
 * name, or an element id's segment (`#k7f3q2xa`, which no name can spell) —
 * deduplicated and in first-seen order. The reader BOTH rename paths need:
 * utils/kql/storedQuery.js feeds it into `renameElements` (so a rename
 * rewrites the reference) and into `rootElementNames` (so the dangling-name
 * check sees it). A stored query that names an element only on the right of a
 * comparison is still naming it.
 */
function refNamesOf(node) {
	const names = [];
	const addValue = value => {
		if (!value || typeof value !== 'object') return;
		if (value.type === 'ref') {
			if (!names.includes(value.v)) names.push(value.v);
			return;
		}
		if (value.type === 'list') (value.v || []).forEach(addValue);
	};
	const walk = n => {
		if (!n || typeof n !== 'object') return;
		if (n.op === 'not') walk(n.arg);
		else if (n.op === 'and' || n.op === 'or') (n.args || []).forEach(walk);
		else if (n.op === 'cmp') addValue(n.value);
	};
	walk(node);
	return names;
}

function conditionCount(node) {
	if (!node || typeof node !== 'object') return 0;
	// A block counts itself and every condition it holds (THE CAP).
	if (node.op === 'cmp') return 1 + (isBlock(node) ? conditionCount(node.value.v) : 0);
	if (node.op === 'not') return conditionCount(node.arg);
	if (node.op === 'and' || node.op === 'or') {
		return (node.args || []).reduce((sum, arg) => sum + conditionCount(arg), 0);
	}
	return 0;
}

// Value constructors — the vendored builder UI composes ASTs with these rather
// than with object literals, so the shape stays in one place.
const str = v => ({ type: 'str', v: String(v) });
const num = v => ({ type: 'num', v: Number(v) });
const date = v => ({ type: 'date', v: String(v) });
const rel = v => ({ type: 'rel', v: String(v).toLowerCase() });
const bool = v => ({ type: 'bool', v: !!v });
const me = () => ({ type: 'me', v: ME });
// `{{name}}` — the other element of this same record. The name only: never
// `elements.<name>`, never a sigil.
const ref = name => ({ type: 'ref', v: String(name) });
const list = values => ({ type: 'list', v: values.slice() });

function cmp(path, comparison, value, { forceElement = false, facet = false } = {}) {
	const node = { op: 'cmp', path: Array.isArray(path) ? path.slice() : [String(path)], cmp: comparison };
	if (value !== undefined && value !== null) node.value = value;
	if (facet) node.facet = true;
	if (forceElement) node.forceElement = true;
	return node;
}

/** `@<card-key> <cmp> <value>` — the facet condition, composed rather than spelled. */
const facet = (key, comparison, value) => cmp([String(key)], comparison, value, { facet: true });

/** A block's value: the query its entries are asked. */
const block = inner => ({ type: BLOCK_VALUE, v: inner });
/**
 * `<group> has (<inner>)` — one entry of the group answers every condition in
 * `inner`. `forceElement` for a group named like a system field (`"card"`).
 */
const has = (group, inner, { forceElement = false } = {}) => cmp(Array.isArray(group) ? group : [String(group)], BLOCK_CMP, block(inner), { forceElement });

const and = (...args) => ({ op: 'and', args: flatten('and', args.flat()) });
const or = (...args) => ({ op: 'or', args: flatten('or', args.flat()) });
const not = arg => ({ op: 'not', arg });

module.exports = {
	// Core API (this is what keepr-web vendors and calls).
	parse,
	serialize,
	KqlError,

	// Helpers.
	isHexId,
	isSystemField,
	systemFieldOf,
	facetKeyOf,
	isElementIdSegment,
	elementIdOf,
	elementIdSegment,
	elementIdsOf,
	conditionCount,
	usesMe,
	usesRef,
	refNamesOf,
	resolveRelativeDate,
	isRelativeLiteral,
	relativeGranularity,
	numberText,
	datePartOf,
	isCreatorPath,
	isSystemDatePart,
	datePartNumber,
	datePartValue,
	datePartSet,
	str,
	num,
	date,
	rel,
	bool,
	me,
	ref,
	list,
	cmp,
	facet,
	and,
	or,
	not,
	isBlock,
	blockOf,
	blockColumnOf,
	isEntryCountPath,
	block,
	has,

	// Shared vocabulary/limits.
	MAX_QUERY_LENGTH,
	MAX_CONDITIONS,
	MAX_PATH_HOPS,
	MAX_PATH_SEGMENTS,
	SYSTEM_FIELDS,
	ME,
	FACET_CMPS,
	IDENT_RE,
	HEX_ID_RE,
	ELEMENT_ID_RE,
	REF_NAME_RE,
	TEMPLATE_NAMESPACES,
	RELATIVE_RE,
	RELATIVE_ANCHORED_RE,
	DATE_RE,
	DATETIME_RE,
	CMP_TEXT,
	VALUE_CMPS,
	LIST_CMPS,
	NULLARY_CMPS,
	BLOCK_CMP,
	BLOCK_VALUE,
	ENTRY_COUNT,
	DATE_PARTS,
	CREATOR_PART,
	PART_ROOTS,
	WEEKDAYS,
	MONTHS,
	DATE_PART_RANGE,
};
